import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Image, Badge, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  Upload,
  Trash2,
  Check,
  Plus,
  User,
  Settings as SettingsIcon,
  Image as ImageIcon,
  Stethoscope,
  Save,
  Eye,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Profile, Logo, PrestazioniProfile } from '../types';

type Section = 'profilo' | 'prestazioni' | 'logo';

// Segnaposto mostrato se l'immagine del logo non si carica
const LOGO_PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><rect width='80' height='80' fill='%23f5e8eb'/><circle cx='40' cy='34' r='10' fill='%23e3c3cb'/><path d='M20 62c4-12 12-16 20-16s16 4 20 16z' fill='%23e3c3cb'/></svg>";

const Settings: React.FC = () => {
  const { profile, updateProfile } = useAuth();
  const [section, setSection] = useState<Section>('profilo');
  const [loading, setLoading] = useState(false);
  const [logos, setLogos] = useState<Logo[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<Logo | null>(null);
  const [prestazioni, setPrestazioni] = useState<PrestazioniProfile[]>([]);
  const [newPrestazione, setNewPrestazione] = useState('');
  const [formData, setFormData] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    email: profile?.email || '',
    codice_fiscale: profile?.codice_fiscale || '',
    piva: profile?.piva || '',
    mobile_number: profile?.mobile_number || '',
    indirizzo: profile?.indirizzo || '',
    citta: profile?.citta || '',
  });

  useEffect(() => {
    loadLogos();
    loadPrestazioni();
  }, []);

  const loadLogos = async () => {
    try {
      const response = await apiService.getLogos();
      setLogos(response.loghi || []);
    } catch (error) {
      toast.error('Errore nel caricamento dei logo');
    }
  };

  const loadPrestazioni = async () => {
    try {
      const response = await apiService.getPrestazioni();
      setPrestazioni(response.results || []);
    } catch (error) {
      toast.error('Errore nel caricamento delle prestazioni');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(formData);
      toast.success('Profilo aggiornato con successo');
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) {
      toast.error('Seleziona un file');
      return;
    }

    try {
      setLoading(true);
      await apiService.uploadLogo(selectedFile);
      toast.success('Logo caricato con successo');
      setSelectedFile(null);
      loadLogos();
    } catch (error) {
      toast.error('Errore durante il caricamento del logo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLogo = async (logoId: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questo logo?')) {
      try {
        await apiService.deleteLogo(logoId);
        toast.success('Logo eliminato con successo');
        loadLogos();
      } catch (error) {
        toast.error('Errore durante l\'eliminazione del logo');
      }
    }
  };

  const handleSelectLogo = async (logoId: number) => {
    try {
      await apiService.selectLogo(logoId);
      toast.success('Logo selezionato con successo');
      loadLogos();
    } catch (error) {
      toast.error('Errore durante la selezione del logo');
    }
  };

  const handleOpenMagnifier = (logo: Logo) => {
    setSelectedLogo(logo);
    setShowMagnifier(true);
  };

  const handleCloseMagnifier = () => {
    setShowMagnifier(false);
    setSelectedLogo(null);
  };

  const handleAddPrestazione = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPrestazione.trim()) {
      toast.error('Inserisci una prestazione');
      return;
    }

    try {
      setLoading(true);
      await apiService.createPrestazione({ prestazione: newPrestazione.trim() });
      toast.success('Prestazione aggiunta con successo');
      setNewPrestazione('');
      loadPrestazioni();
    } catch (error) {
      toast.error('Errore durante l\'aggiunta della prestazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrestazione = async (prestazioneId: number, prestazioneName: string) => {
    if (window.confirm(`Sei sicuro di voler eliminare la prestazione "${prestazioneName}"?`)) {
      try {
        setLoading(true);
        await apiService.deletePrestazione(prestazioneId);
        toast.success('Prestazione eliminata con successo');
        loadPrestazioni();
      } catch (error) {
        toast.error('Errore durante l\'eliminazione della prestazione');
      } finally {
        setLoading(false);
      }
    }
  };

  const initials = `${formData.first_name?.[0] || ''}${formData.last_name?.[0] || ''}`.toUpperCase();

  const tabs: { id: Section; label: string; hint: string; icon: React.ReactNode; count?: number }[] = [
    { id: 'profilo', label: 'Profilo', hint: 'Dati personali e fiscali', icon: <User size={20} /> },
    { id: 'prestazioni', label: 'Prestazioni', hint: 'Voci per fatture e referti', icon: <Stethoscope size={20} />, count: prestazioni.length },
    { id: 'logo', label: 'Logo', hint: 'Intestazione dei documenti', icon: <ImageIcon size={20} />, count: logos.length },
  ];

  return (
    <div className="settings-page">
      <Container fluid className="py-4">
        <Row className="mb-4 page-banner">
          <Col>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="settings-icon-wrapper">
                <SettingsIcon size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="mb-1 fw-bold text-dark">Impostazioni</h1>
                <p className="text-muted mb-0">Gestisci il tuo profilo e le configurazioni dell'applicazione</p>
              </div>
            </div>
          </Col>
        </Row>

        <div className="set-layout">
          <aside className="set-side">
            <div className="set-me">
              <div className="set-avatar">{initials || <User size={26} />}</div>
              <strong>{formData.first_name} {formData.last_name}</strong>
              <span>{formData.email}</span>
            </div>

            <nav className="set-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`set-tab ${section === tab.id ? 'active' : ''}`}
                  onClick={() => setSection(tab.id)}
                >
                  <span className="set-tab-icon">{tab.icon}</span>
                  <span className="set-tab-text">
                    <strong>{tab.label}</strong>
                    <small>{tab.hint}</small>
                  </span>
                  {tab.count !== undefined && <span className="set-tab-count">{tab.count}</span>}
                </button>
              ))}
            </nav>
          </aside>

          <section className="set-panel">
            {section === 'profilo' && (
              <Form onSubmit={handleSubmit}>
                <header className="set-head">
                  <h2>Profilo</h2>
                  <p>Questi dati compaiono su fatture e referti.</p>
                </header>

                <div className="set-group">
                  <h3>Dati personali</h3>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Cognome</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="set-group">
                  <h3>Dati fiscali</h3>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Codice Fiscale</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.codice_fiscale}
                          onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>P.IVA</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.piva}
                          onChange={(e) => setFormData({ ...formData, piva: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="set-group">
                  <h3>Sede</h3>
                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Indirizzo</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.indirizzo}
                          onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Città</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.citta}
                          onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="set-savebar">
                  <Button type="submit" variant="primary" disabled={loading}>
                    <Save size={16} />
                    {loading ? 'Salvando...' : 'Salva modifiche'}
                  </Button>
                </div>
              </Form>
            )}

            {section === 'prestazioni' && (
              <div>
                <header className="set-head">
                  <h2>Prestazioni</h2>
                  <p>Le voci che puoi aggiungere a fatture e referti.</p>
                </header>

                <Form onSubmit={handleAddPrestazione} className="set-add">
                  <Form.Control
                    type="text"
                    placeholder="Nome della prestazione, es. Visita cardiologica"
                    value={newPrestazione}
                    onChange={(e) => setNewPrestazione(e.target.value)}
                    disabled={loading}
                  />
                  <Button type="submit" variant="primary" disabled={!newPrestazione.trim() || loading}>
                    <Plus size={16} /> Aggiungi
                  </Button>
                </Form>

                {prestazioni.length === 0 ? (
                  <div className="set-empty">
                    <Stethoscope size={34} />
                    <strong>Nessuna prestazione inserita</strong>
                    <span>Aggiungi la tua prima prestazione per iniziare.</span>
                  </div>
                ) : (
                  <ul className="set-chips">
                    {prestazioni.map((prestazioneProfile) => (
                      <li key={prestazioneProfile.id} className="set-chip">
                        <span className="set-chip-icon"><Stethoscope size={16} /></span>
                        <span className="set-chip-name">{prestazioneProfile.prestazione.prestazione}</span>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeletePrestazione(
                            prestazioneProfile.prestazione.id,
                            prestazioneProfile.prestazione.prestazione
                          )}
                          disabled={loading}
                          title="Elimina prestazione"
                          className="btn-sm-modern"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {section === 'logo' && (
              <div>
                <header className="set-head">
                  <h2>Logo</h2>
                  <p>Il logo attivo appare nell'intestazione di fatture e referti.</p>
                </header>

                <div className="set-upload">
                  <div className="set-upload-field">
                    <Upload size={22} />
                    <div>
                      <strong>{selectedFile ? selectedFile.name : 'Carica un nuovo logo'}</strong>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleUploadLogo}
                    disabled={!selectedFile || loading}
                  >
                    <Upload size={16} /> Carica
                  </Button>
                </div>

                {logos.length === 0 ? (
                  <div className="set-empty">
                    <ImageIcon size={34} />
                    <strong>Nessun logo caricato</strong>
                    <span>Carica il tuo primo logo per iniziare.</span>
                  </div>
                ) : (
                  <div className="set-logos">
                    {logos.map((logo) => (
                      <article key={logo.id} className={`set-logo ${logo.is_active ? 'is-active' : ''}`}>
                        <button
                          type="button"
                          className="set-logo-img"
                          onClick={() => handleOpenMagnifier(logo)}
                          title="Clicca per ingrandire"
                        >
                          <Image
                            src={logo.image_url || logo.image}
                            alt="Logo"
                            onError={(e) => {
                              const target = e.currentTarget as HTMLImageElement;
                              target.src = LOGO_PLACEHOLDER;
                              target.style.opacity = '0.5';
                              target.title = 'Errore nel caricamento del logo';
                              console.warn('⚠️ Logo non disponibile:', logo.image_url || logo.image);
                            }}
                          />
                          {logo.is_active && (
                            <Badge bg="success" className="set-logo-badge">
                              <Check size={12} /> Attivo
                            </Badge>
                          )}
                        </button>
                        <div className="set-logo-foot">
                          <small>{logo.date ? new Date(logo.date).toLocaleDateString() : 'Data non disponibile'}</small>
                          <div className="d-flex gap-1">
                            {!logo.is_active && (
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => handleSelectLogo(logo.id)}
                                className="btn-sm-modern"
                                title="Seleziona come attivo"
                              >
                                <Check size={14} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-info"
                              onClick={() => handleOpenMagnifier(logo)}
                              className="btn-sm-modern"
                              title="Anteprima"
                            >
                              <Eye size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteLogo(logo.id)}
                              className="btn-sm-modern"
                              title="Elimina logo"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Modal Magnifier per Logo */}
        <Modal
          show={showMagnifier}
          onHide={handleCloseMagnifier}
          size="lg"
          centered
          className="modern-modal"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="d-flex align-items-center gap-2">
              <Eye size={20} />
              Anteprima Logo
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            {selectedLogo && (
              <div>
                <div className="logo-magnifier-container mb-4">
                  <Image
                    src={selectedLogo.image_url || selectedLogo.image}
                    alt="Logo ingrandito"
                    className="logo-magnifier-image"
                    fluid
                  />
                </div>
                <div className="logo-info">
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                    <small className="text-muted">
                      Caricato il: {selectedLogo.date ? new Date(selectedLogo.date).toLocaleDateString() : 'Data non disponibile'}
                    </small>
                    {selectedLogo.is_active && (
                      <Badge bg="success" className="d-flex align-items-center gap-1">
                        <Check size={12} /> Attivo
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button
              variant="secondary"
              onClick={handleCloseMagnifier}
              className="btn-modern d-flex align-items-center gap-2"
            >
              <X size={16} />
              Chiudi
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Settings;
